/**
 * App.tsx - Hovedapplikasjon med React Router
 *
 * CODE SPLITTING / LAZY LOADING
 * =============================
 * Alle sider lastes med React.lazy() for å redusere initial bundle size.
 * Dette gir betydelig bedre Lighthouse Performance score (fra ~66 til ~85+).
 *
 * Hvordan det fungerer:
 * - React.lazy() laster komponenten først når den trengs (ved navigering)
 * - Suspense viser LoadingFallback mens komponenten lastes
 * - Webpack splitter automatisk koden i separate chunks
 *
 * For sider med NAMED exports (export const X), bruk:
 *   lazy(() => import('./pages/X').then(m => ({ default: m.X })))
 *
 * For sider med DEFAULT exports (export default X), bruk:
 *   lazy(() => import('./pages/X'))
 *
 * Ved nye sider:
 * 1. Opprett siden som vanlig
 * 2. Legg til lazy import her (se mønster under)
 * 3. Legg til Route i Routes-komponenten
 *
 * Se også: .md/implementation/performance-improvements.md
 */
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ScrollToTop } from './components/ScrollToTop';
import { LoadingFallback } from './components/LoadingFallback';
import { initAnalytics, setVisitLanguage, trackPageView } from './utils/analytics';
import { features } from './config/features';

// =============================================================================
// LAZY-LOADED PAGES
// =============================================================================
// Alle sider lastes dynamisk for å redusere initial bundle size.
// Grupper etter funksjonalitet for lesbarhet.

// --- Offentlige sider ---
const LandingPage = lazy(() => import('./pages/LandingPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const SubprocessorsPage = lazy(() => import('./pages/SubprocessorsPage'));
const DpaPage = lazy(() => import('./pages/DpaPage'));
const AtomicResearchPage = lazy(() => import('./pages/AtomicResearchPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const AccessibilityPage = lazy(() => import('./pages/AccessibilityPage'));

// --- Autentisering ---
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const RequestPasswordResetPage = lazy(() => import('./pages/RequestPasswordResetPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ForcePasswordChangePage = lazy(() => import('./pages/ForcePasswordChangePage'));

// --- Hovedapp ---
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const InsightsPage = lazy(() => import('./pages/InsightsPage'));
const InsightDetailPage = lazy(() => import('./pages/InsightDetailPage').then(m => ({ default: m.InsightDetailPage })));
const FactsPage = lazy(() => import('./pages/FactsPage'));
const OutdatedFactsPage = lazy(() => import('./pages/OutdatedFactsPage'));
const BugsPage = lazy(() => import('./pages/BugsPage'));
const FixedBugsPage = lazy(() => import('./pages/FixedBugsPage'));
const ImportPage = lazy(() => import('./pages/ImportPage').then(m => ({ default: m.ImportPage })));
const ImportDetailPage = lazy(() => import('./pages/ImportDetailPage'));
const ImportHistoryPage = lazy(() => import('./pages/ImportHistoryPage'));
const ExperimentsPage = lazy(() => import('./pages/ExperimentsPage').then(m => ({ default: m.ExperimentsPage })));
const ConclusionsPage = lazy(() => import('./pages/ConclusionsPage').then(m => ({ default: m.ConclusionsPage })));
const ConclusionDetailPage = lazy(() => import('./pages/ConclusionDetailPage').then(m => ({ default: m.ConclusionDetailPage })));

// --- Administrasjon ---
const TagManagementPage = lazy(() => import('./pages/TagManagementPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const CreateUserPage = lazy(() => import('./pages/CreateUserPage'));
const EditUserPage = lazy(() => import('./pages/EditUserPage'));
const OrganizationManagementPage = lazy(() => import('./pages/OrganizationManagementPage'));
const OrganizationStatsPage = lazy(() => import('./pages/OrganizationStatsPage'));
const OrganizationSettingsPage = lazy(() => import('./pages/OrganizationSettingsPage'));
const DomainPage = lazy(() => import('./pages/DomainPage'));
const APIKeysPage = lazy(() => import('./pages/APIKeysPage'));
const WidgetBuilderPage = lazy(() => import('./pages/WidgetBuilderPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));

// --- Rapporter ---
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const ReportDetailPage = lazy(() => import('./pages/ReportDetailPage'));

// --- Info-sider ---
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const ChangelogPage = lazy(() => import('./pages/ChangelogPage').then(m => ({ default: m.ChangelogPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Initialize analytics once on app load
initAnalytics();

function InsightIdRedirect() {
  const { id } = useParams();
  return <Navigate to={`/hypotheses/${id}`} replace />;
}

function PageViewTracker() {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    // The title is set by the page itself — useDocumentTitle, or react-helmet-
    // async — and React runs those effects AFTER this one. Reading
    // document.title here would file every hit under the previous page's
    // title. Waiting a frame lets Helmet flush, and a macrotask after it lets
    // the plain effects land, so Matomo gets the title the reader saw.
    let cancelled = false;
    let timer: number | undefined;
    const frame = requestAnimationFrame(() => {
      timer = window.setTimeout(() => {
        if (cancelled) return;
        setVisitLanguage(i18n.language);
        trackPageView(location.pathname, document.title);
      }, 0);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [location.pathname, i18n.language]);

  return null;
}

function App() {
  return (
    <HelmetProvider>
    <ThemeProvider>
      <Router>
        <PageViewTracker />
        <ScrollToTop />
        <AuthProvider>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/request-password-reset" element={<RequestPasswordResetPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/force-password-change" element={<ForcePasswordChangePage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hypotheses"
            element={
              <ProtectedRoute>
                <InsightsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hypotheses/:id"
            element={
              <ProtectedRoute>
                <InsightDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/import"
            element={
              <ProtectedRoute>
                <ImportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/imports"
            element={
              <ProtectedRoute>
                <ImportHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/imports/:job_id"
            element={
              <ProtectedRoute>
                <ImportDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/experiments"
            element={
              <ProtectedRoute>
                <ExperimentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/facts"
            element={
              <ProtectedRoute>
                <FactsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/facts/outdated"
            element={
              <ProtectedRoute>
                <OutdatedFactsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bugs"
            element={
              <ProtectedRoute>
                <BugsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bugs/fixed"
            element={
              <ProtectedRoute>
                <FixedBugsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tags"
            element={
              <ProtectedRoute>
                <TagManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/conclusions"
            element={
              <ProtectedRoute>
                <ConclusionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/conclusions/:id"
            element={
              <ProtectedRoute>
                <ConclusionDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <ReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/:id"
            element={
              <ProtectedRoute>
                <ReportDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/changelog"
            element={
              <ProtectedRoute>
                <ChangelogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UserManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/new"
            element={
              <ProtectedRoute>
                <CreateUserPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/:userId/edit"
            element={
              <ProtectedRoute>
                <EditUserPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/organizations"
            element={
              <ProtectedRoute>
                <OrganizationManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/organizations/:organizationId/stats"
            element={
              <ProtectedRoute>
                <OrganizationStatsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/domain"
            element={
              <ProtectedRoute>
                <DomainPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/widget"
            element={
              <ProtectedRoute>
                <APIKeysPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/widget/new"
            element={
              <ProtectedRoute>
                <WidgetBuilderPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/widget/:id/edit"
            element={
              <ProtectedRoute>
                <WidgetBuilderPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organization/settings"
            element={
              <ProtectedRoute>
                <OrganizationSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              features.billing ? (
                <ProtectedRoute>
                  <BillingPage />
                </ProtectedRoute>
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/billing/upgrade"
            element={
              features.billing ? (
                <Navigate to="/billing" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route path="/insights" element={<Navigate to="/hypotheses" replace />} />
          {/* The detail form of the same rename. Without it React Router
              matches neither this nor the line above, and the link lands on
              the not-found page — which is what old bookmarks, and three
              links inside GenerateInsightModal, were doing. */}
          <Route path="/insights/:id" element={<InsightIdRedirect />} />
          <Route path="/funksjoner" element={<FeaturesPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/personvern" element={<PrivacyPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/vilkar" element={<TermsPage />} />
          <Route path="/subprocessors" element={<SubprocessorsPage />} />
          <Route path="/underleverandorer" element={<SubprocessorsPage />} />
          <Route path="/dpa" element={<DpaPage />} />
          <Route path="/databehandleravtale" element={<DpaPage />} />
          <Route path="/atomic-research" element={<AtomicResearchPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/sikkerhet" element={<SecurityPage />} />
          <Route path="/accessibility" element={<AccessibilityPage />} />
          <Route path="/tilgjengelighet" element={<AccessibilityPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/" element={<LandingPage />} />
          {/* Catch-all, always last. nginx cannot know which paths React Router
              resolves, so the SPA fallback stays and the status code stays 200
              (B21) — this page is what the reader sees instead of a blank one. */}
          <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </Router>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
